import { CacheProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import { apiPlugin, storyblokInit } from '@storyblok/react';
import React, { useEffect, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import {
  createStaticHandler,
  createStaticRouter,
  RouterProvider,
  StaticRouterProvider,
} from 'react-router';

import config from './config';
import { ContextProvider } from './ContextProvider';
import { createAppRouter, createDataRouterRoutes } from './dataRouter';
import configureStore from './store';
import { createTheme } from './styles/mui/create-theme';
import { captureAttributionFromUrl } from './util/consent/attribution';
import initConsent from './util/consent/initConsent';
import { applyDateLocale, whenMomentLocaleReady } from './util/dateLocale';
import { resolveAppLocale } from './util/locale';
import { capturePartnerRefFromUrl } from './util/partnerRef';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);

/**
 * This is the namespace for the VanSite application.
 * Everything related to the application should be under this namespace.
 *
 * The application is built using the Sharetribe Flex Template Web.
 *
 * Note: I hope you have fun working with the codebase!
 */

// Flex template application uses English translations as default.
// If you want to change the language,K change the imports to match the wanted locale:
//   1) Change the language in the config.js file!
//   2) Import correct locale rules for Moment library
//   3) Use the `messagesInLocale` import to add the correct translation file.
// Note that there is also translations in './translations/countryCodes.js' file
// This file contains ISO 3166-1 alpha-2 country codes, country names and their translations in our default languages
// This used to collect billing address in StripePaymentAddress on CheckoutPage
// Step 2:
// If you are using a non-english locale with moment library,
// you should also import time specific formatting rules for that locale
// e.g. for French: import 'moment/locale/fr';
// Step 3:
// If you are using a non-english locale, point `messagesInLocale` to correct .json file

const isTestEnv = process.env.NODE_ENV === 'test';

/**
 * Applies the marketplace locale to the date libraries and returns it.
 *
 * @param {string} [routeLocale] the locale-carrying route: a pathname on the
 *   client, `serverContext.locale` on the server. Omitted only in tests.
 * @returns {string|undefined} a key of `config.languages`
 */
const setupLocale = (routeLocale) => {
  if (isTestEnv) {
    // Use english as a default locale in tests
    // This affects app.test.js and app.node.test.js tests
    config.locale = 'en';
    return;
  }

  // Set the date-library locale globally. dayjs (main bundle) is applied
  // synchronously; moment is applied asynchronously so it stays out of the main
  // bundle — see util/dateLocale.js.
  const locale = resolveAppLocale({
    pathname: routeLocale,
    navigatorLanguage: typeof navigator !== 'undefined' ? navigator.language : undefined,
  });

  applyDateLocale(locale);
  return locale;
};

/**
 * The route the client should resolve its locale from.
 *
 * Cordova runs a hash router (see `createAppRouter`), so there the locale
 * prefix lives in the hash and `location.pathname` is the asset path.
 */
const currentRoutePath = () => {
  if (typeof window === 'undefined') return undefined;
  const { hash, pathname } = window.location;
  return hash && hash.startsWith('#/') ? hash.slice(1) : pathname;
};

const setupStoryblok = () => {
  storyblokInit({
    accessToken: process.env.REACT_APP_STORYBLOK_ACCESS_TOKEN,
    use: [apiPlugin],
    apiOptions: {
      region: 'eu',
    },
    bridge: process.env.REACT_APP_ENV !== 'production',
  });
};

export const ClientApp = ({ store, cache, messages = null }) => {
  const locale = setupLocale(currentRoutePath());
  setupStoryblok();

  // Create data router — loaders dispatch to Redux store before rendering
  const router = useMemo(() => createAppRouter(store), [store]);

  useEffect(() => {
    initConsent();
    captureAttributionFromUrl();
    capturePartnerRefFromUrl();
  }, []);

  const deviceType = navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i) ? 'mobile' : 'desktop';

  return (
    <ContextProvider.Provider value={{
      locale,
      deviceType,
      messages,
    }}>
      <Provider store={store}>
        <CacheProvider value={cache}>
          <ThemeProvider theme={createTheme(deviceType)}>
            <CssBaseline />
            <RouterProvider router={router} />
          </ThemeProvider>
        </CacheProvider>
      </Provider>
    </ContextProvider.Provider>
  );
};

/**
 * Render the given route using React Router v7 data mode SSR.
 * Uses the SAME route structure as ClientApp (createDataRouterRoutes)
 * to ensure hydration produces identical component trees.
 */
export const renderApp = async (
  url,
  serverContext,
  preloadedState,
  collectChunks,
  emotionCache,
  deviceType,
) => {
  // serverContext.locale is the first path segment (server/index.js) — the same
  // value the client reads out of window.location, so both sides format dates
  // identically and hydration stays clean.
  setupLocale(serverContext.locale);
  // Lazy chunks resolve synchronously on the server, so a moment-formatting
  // component (PassCard, StoryblokChangedAt, …) would render with moment's
  // default locale and mismatch on hydration unless we wait here.
  await whenMomentLocaleReady();
  setupStoryblok();

  // Store with preloaded data (no SDK — data already loaded by server/dataLoader.js)
  const store = configureStore(preloadedState, null, [], null);

  // Preload translation messages on the server
  const loadMessages = async () => {
    switch (serverContext.locale) {
      case 'de':
        return await import(/* webpackChunkName: "messages_de" */'./translations/compiled/de.json');
      case 'en':
        return await import(/* webpackChunkName: "messages_en" */'./translations/compiled/en.json');
      case 'es':
        return await import(/* webpackChunkName: "messages_es" */'./translations/compiled/es.json');
      case 'fr':
        return await import(/* webpackChunkName: "messages_fr" */'./translations/compiled/fr.json');
      case 'it':
        return await import(/* webpackChunkName: "messages_it" */'./translations/compiled/it.json');
      case 'no':
        return await import(/* webpackChunkName: "messages_no" */'./translations/compiled/no.json');
      case 'sv':
        return await import(/* webpackChunkName: "messages_sv" */'./translations/compiled/sv.json');
      default:
        return await import(/* webpackChunkName: "messages_en" */'./translations/compiled/en.json');
    }
  };

  const messagesModule = await loadMessages();
  const messages = messagesModule.default || messagesModule;

  // Build the SAME route structure used by ClientApp.
  // All loaders return null during SSR (typeof window === 'undefined'),
  // so query() only does route matching — no data loading.
  const routes = createDataRouterRoutes(store);
  const { query, dataRoutes } = createStaticHandler(routes);

  // Convert URL to a Web Request for the static handler
  // Include Accept-Language so the catch-all locale redirect loader can detect the correct locale
  const fetchRequest = new Request(new URL(url, 'http://localhost'), {
    headers: serverContext.locale ? { 'Accept-Language': serverContext.locale } : {},
  });
  const context = await query(fetchRequest);

  // If a loader threw a redirect (e.g., locale catch-all), return it
  if (context instanceof Response) {
    // Early exit: release the store's reducer-registry subscription here too,
    // the try/finally around renderToString below is never reached.
    store.unsubscribeFromReducerRegistry?.();
    return { redirect: context.headers.get('Location'), status: context.status };
  }

  // Create the static router from the matched context
  const router = createStaticRouter(dataRoutes, context);

  // Include messages in serverContext so LocaleLayout can read them
  const contextWithMessages = { ...serverContext, messages };

  const WithChunks = collectChunks(
    <ContextProvider.Provider value={contextWithMessages}>
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <ThemeProvider theme={createTheme(deviceType)}>
            <CssBaseline />
            <StaticRouterProvider context={context} hydrate={false} router={router} />
          </ThemeProvider>
        </CacheProvider>
      </Provider>
    </ContextProvider.Provider>,
  );

  let body;
  try {
    body = ReactDOMServer.renderToString(WithChunks);
  } finally {
    // Per-request server store: release the reducer-registry subscription taken in
    // configureStore, otherwise every request's store stays referenced for the process lifetime.
    store.unsubscribeFromReducerRegistry?.();
  }
  const head = Helmet.renderStatic();

  // Return hydration data separately so renderer.js can inject it OUTSIDE
  // #root. With hydrate={true} (default), StaticRouterProvider injects a
  // <script> tag INSIDE the React tree — but the client's RouterProvider
  // doesn't render that node, creating a structural mismatch that breaks
  // React hydration.
  const hydrationData = {
    loaderData: context.loaderData,
    actionData: context.actionData || null,
    errors: context.errors || null,
  };

  return { head, body, hydrationData };
};
